import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuth } from "../../../core/auth";
import { HiXMark, HiFlag } from "react-icons/hi2";
import { GET_ORDERS } from "../../order/page";
import type { OrdersQuery } from "../../../__generated__/graphql";

const STORAGE_KEY = "dismissedPendingPackOrderIds";

function isToday(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = new Date(date);
  const t = new Date();
  return (
    d.getDate() === t.getDate() &&
    d.getMonth() === t.getMonth() &&
    d.getFullYear() === t.getFullYear()
  );
}

function toHHmm(date: Date | string): string {
  const d = new Date(date);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

const TodayPendingPackOrderInfoBar = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { data, loading } = useQuery<OrdersQuery>(GET_ORDERS, {
    skip: !isLoggedIn,
  });
  const [dismissedOrderId, setDismissedOrderId] = useState<number | null>(null);

  const dismissedOrderIds = useMemo(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const todayPendingOrder = useMemo(() => {
    if (loading || !data?.orders?.length) return null;

    const orders = data.orders;

    const pending = orders.filter(
      (o) =>
        o.packId != null &&
        !o.isCanceled &&
        o.isPackConfirmed === false &&
        o.packPickupStartTime != null &&
        isToday(o.packPickupStartTime) &&
        !dismissedOrderIds.includes(o.id) &&
        dismissedOrderId !== o.id,
    );

    if (pending.length === 0) return null;
    pending.sort(
      (a, b) =>
        new Date(a.packPickupStartTime!).getTime() -
        new Date(b.packPickupStartTime!).getTime(),
    );
    return pending[0];
  }, [data?.orders, loading, dismissedOrderIds, dismissedOrderId]);

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!todayPendingOrder) return;
    try {
      const current = dismissedOrderIds.includes(todayPendingOrder.id)
        ? dismissedOrderIds
        : [...dismissedOrderIds, todayPendingOrder.id];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      setDismissedOrderId(todayPendingOrder.id);
    } catch (err) {
      console.error("Failed to save dismissed order id:", err);
    }
  };

  const handleClick = () => {
    if (todayPendingOrder) navigate(`/order/${todayPendingOrder.id}`);
  };

  if (!isLoggedIn || !todayPendingOrder) return null;

  const confirmTime = todayPendingOrder.packAutoConfirmTime
    ? toHHmm(todayPendingOrder.packAutoConfirmTime)
    : todayPendingOrder.packPickupStartTime
      ? (() => {
          const start = new Date(todayPendingOrder.packPickupStartTime!);
          start.setMinutes(start.getMinutes() - 30);
          return toHHmm(start);
        })()
      : "-";
  const reserveStart = todayPendingOrder.packPickupStartTime
    ? toHHmm(todayPendingOrder.packPickupStartTime)
    : "-";
  const reserveEnd = todayPendingOrder.packPickupEndTime
    ? toHHmm(todayPendingOrder.packPickupEndTime)
    : "-";

  return (
    <div className="fixed bottom-20 left-0 right-0 z-40 px-4 max-w-[742px] mx-auto">
      <div
        onClick={handleClick}
        className="bg-black/80 backdrop-blur-sm rounded-lg py-5 px-4 flex items-center gap-3 cursor-pointer hover:bg-black/90 transition-colors"
      >
        <HiFlag className="w-6 h-6 text-secondary flex-shrink-0 animate-floatBounce" />
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-base">주문 알림</p>
          <p className="text-white/90 text-sm mt-0.5 truncate">
            확정시간: {confirmTime} / 픽업시간: {reserveStart}~{reserveEnd}
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white hover:text-gray-300 transition-colors flex-shrink-0"
          aria-label="닫기"
        >
          <HiXMark className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default TodayPendingPackOrderInfoBar;
